import React, { useState, useMemo, useRef, useEffect } from "react";
import { Search, Eye, EyeOff, GripVertical, ChevronDown, ChevronUp } from "lucide-react";
import { useFilterStore, type ColumnKey } from "../../store/filterStore";
import { useInstanceStore } from "../../store/useInstanceStore";
import type { Instance } from "../table/types";

interface SmartFiltersAccordionProps {
  isOpen: boolean;
  onClose?: () => void;
  buttonRef?: React.RefObject<HTMLButtonElement | null>;
  showCPUArchitectureFilter?: boolean;
}

interface ColumnConfig {
  key: ColumnKey;
  label: string;
}

const columnConfigs: ColumnConfig[] = [
  { key: "instanceType", label: "Instance Type" },
  { key: "instanceFamily", label: "Instance Family" },
  { key: "vcpus", label: "vCPUs" },
  { key: "memory", label: "Memory (GB)" },
  { key: "cpuArchitecture", label: "CPU Architecture" },
  { key: "gpu", label: "GPU (GB)" },
  { key: "gpuName", label: "GPU Name" },
  { key: "aws", label: "AWS Price" },
  { key: "gcp", label: "GCP Price" },
  { key: "azure", label: "Azure Price" },
  { key: "best", label: "Best" },
];

const SmartFiltersAccordion: React.FC<SmartFiltersAccordionProps> = ({
  isOpen,
  onClose,
  buttonRef,
  showCPUArchitectureFilter = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isCpuArchitectureFilterOpen, setIsCpuArchitectureFilterOpen] = useState(true);
  const popoverRef = useRef<HTMLDivElement>(null);
  const data = useInstanceStore((state) => state.data);
  const columnVisibility = useFilterStore((state) => state.columnVisibility);
  const toggleColumnVisibility = useFilterStore(
    (state) => state.toggleColumnVisibility
  );
  const resetColumnVisibility = useFilterStore(
    (state) => state.resetColumnVisibility
  );
  const cpuArchitecture = useFilterStore((state) => state.cpuArchitecture);
  const setCpuArchitecture = useFilterStore((state) => state.setCpuArchitecture);

  // Get unique CPU architectures from data
  const cpuArchitectureOptions = useMemo(() => {
    if (!data || data.length === 0) return [];
    const uniqueArchs = new Set<string>();
    data.forEach((instance: Instance) => {
      if (instance.cpuArchitecture) {
        uniqueArchs.add(instance.cpuArchitecture);
      }
    });
    const archs = Array.from(uniqueArchs).sort();
    return [
      { value: "", label: "All Architectures" },
      ...archs.map((arch) => ({ value: arch, label: arch })),
    ];
  }, [data]);

  // Filter columns based on search
  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) return columnConfigs;
    const query = searchQuery.toLowerCase();
    return columnConfigs.filter((config) =>
      config.label.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Reset column visibility to default
  const handleReset = () => {
    resetColumnVisibility();
    setSearchQuery("");
  };

  // Close popover when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef?.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, buttonRef]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      {/* Popover */}
      <div
        ref={popoverRef}
        className="absolute right-0 top-full mt-2 z-50 w-80 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
        style={{ minWidth: "320px" }}
      >
        <div className="max-h-96 overflow-y-auto">
          <div className="p-3 space-y-3">

            {showCPUArchitectureFilter && (
              <div >
                <button
                  onClick={() => setIsCpuArchitectureFilterOpen(!isCpuArchitectureFilterOpen)}
                  className="w-full flex items-center justify-between px-2 py-2 hover:bg-gray-50 rounded-md transition-colors"
                >
                  <span className="text-sm font-semibold text-gray-900">
                    CPU Architecture Filter
                  </span>
                  {isCpuArchitectureFilterOpen ? (
                    <ChevronUp size={16} className="text-gray-600" />
                  ) : (
                    <ChevronDown size={16} className="text-gray-600" />
                  )}
                </button>

                {isCpuArchitectureFilterOpen && (
                  <div className="mt-3 px-2">
                    <select
                      value={cpuArchitecture}
                      onChange={(e) => setCpuArchitecture(e.target.value)}
                      className="w-full h-10 border border-gray-300 text-gray-700 text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      {cpuArchitectureOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* ============= Manage Fields Mode ============= */}
            {!showCPUArchitectureFilter && (
              <>
                {/* Search Bar */}
                <div className="border-b border-gray-200 pb-3">
                  <div className="relative flex items-center gap-2">
                    <div className="absolute left-3 pointer-events-none">
                      <Search size={16} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-16 py-2 border border-gray-300 rounded-md text-sm text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
                    />

                    <button
                      onClick={handleReset}
                      className="absolute right-2 px-2 py-1 text-xs text-gray-600 hover:bg-gray-900 hover:bg-gray-100 rounded"
                    >
                      Reset
                    </button>
                  </div>
                </div>

                {/* Field Visibility List */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Fields</h3>
                  <div className="space-y-1">
                    {filteredColumns.map(({ key, label }) => {
                      const isVisible = columnVisibility[key];
                      return (
                        <div
                          key={key}
                          className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-md cursor-pointer group"
                          onClick={() => toggleColumnVisibility(key)}
                        >
                          <div className="text-gray-400 group-hover:text-gray-600 cursor-grab">
                            <GripVertical size={16} />
                          </div>

                          <span className="flex-1 text-sm text-gray-700">
                            {label}
                          </span>

                          <div>
                            {isVisible ? (
                              <Eye size={16} className="text-gray-600" />
                            ) : (
                              <EyeOff size={16} className="text-gray-400" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

          </div>
        </div>

      </div>
    </>
  );
};

export default SmartFiltersAccordion;

